import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import {
	IconAlertTriangle,
	IconBuilding,
	IconCalendarX,
	IconFileExport,
	IconFileSpreadsheet,
	IconFileTypeCsv,
	IconFileTypePdf,
	IconUser,
} from "@tabler/icons-react";
import React from "react";
import type { FC } from "react";

interface ExpiringContractsProps {
	contracts: SalesContract[];
	className?: string;
}

export const ExpiringContracts: FC<ExpiringContractsProps> = ({ contracts, className = "" }) => {
	const handleExport = (format: "csv" | "xlsx" | "pdf") => {
		alert("wala pa :)");
	};

	if (!contracts || contracts.length === 0) {
		return (
			<Card className={className}>
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between">
						<CardTitle className="flex items-center gap-2 text-orange-800">
							<IconCalendarX className="h-5 w-5" />
							Contracts Expiring Within 30 Days
						</CardTitle>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="sm">
									<IconFileExport className="w-3 h-3 mr-1" />
									Export
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleExport("csv")}>
									<IconFileTypeCsv className="w-4 h-4 mr-2" />
									Export as CSV
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("xlsx")}>
									<IconFileSpreadsheet className="w-4 h-4 mr-2" />
									Export as Excel
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("pdf")}>
									<IconFileTypePdf className="w-4 h-4 mr-2" />
									Export as PDF
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</CardHeader>
				<CardContent>
					<div className="text-center py-6">
						<div className="text-gray-500 text-sm">No contracts expiring within 30 days</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	const getStatusBadge = (status: string) => {
		const colorMap: Record<string, string> = {
			active: "green",
			expired: "red",
			cancelled: "gray",
		};
		return <Badge color={colorMap[status] || "default"}>{status}</Badge>;
	};

	const getDaysUntilExpiry = (endDate: string) => {
		const today = new Date();
		const expiry = new Date(endDate);
		const diffTime = expiry.getTime() - today.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const getDaysUntilExpiryBadge = (endDate: string) => {
		const days = getDaysUntilExpiry(endDate);

		if (days < 0) {
			const expiredDays = Math.abs(days);
			return (
				<Badge color="red">
					Expired {expiredDays} day{expiredDays !== 1 ? "s" : ""} ago
				</Badge>
			);
		}
		if (days === 0) {
			return <Badge color="red">Expires Today</Badge>;
		}
		if (days <= 7) {
			return <Badge color="red">{days} days left</Badge>;
		}
		if (days <= 15) {
			return <Badge color="orange">{days} days left</Badge>;
		}
		return <Badge color="yellow">{days} days left</Badge>;
	};

	return (
		<Card className={className}>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-orange-800">
						<IconAlertTriangle className="h-5 w-5" />
						Contracts Expiring Within 30 Days ({contracts.length})
					</CardTitle>
					{contracts.length > 0 && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="sm">
									<IconFileExport className="w-3 h-3 mr-1" />
									Export
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleExport("csv")}>
									<IconFileTypeCsv className="w-4 h-4 mr-2" />
									Export as CSV
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("xlsx")}>
									<IconFileSpreadsheet className="w-4 h-4 mr-2" />
									Export as Excel
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("pdf")}>
									<IconFileTypePdf className="w-4 h-4 mr-2" />
									Export as PDF
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</CardHeader>
			<CardContent>
				<div className="space-y-3">
					{contracts.map((contract) => (
						<div key={contract.id} className="border border-orange-200 rounded-lg p-4 bg-orange-50/30 hover:bg-orange-50/60 transition-colors">
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<div className="flex items-center gap-3 mb-2">
										<h4 className="font-medium text-gray-900">{contract.project_name}</h4>
										{getStatusBadge(contract.status)}
										{getDaysUntilExpiryBadge(contract.end_date)}
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
										<div className="flex items-center gap-2">
											<IconBuilding className="h-4 w-4" />
											<span>{contract.client_name}</span>
										</div>

										{contract.salesperson && (
											<div className="flex items-center gap-2">
												<IconUser className="h-4 w-4" />
												<span>{contract.salesperson.name}</span>
											</div>
										)}

										<div>
											<span className="font-medium">Contract Amount:</span> ₱{formatNumber(contract.contract_amount)}
										</div>

										<div>
											<span className="font-medium">Monthly Recurring:</span> ₱{formatNumber(contract.monthly_recurring_sales)}
										</div>
									</div>

									<div className="mt-3 pt-3 border-t border-orange-200">
										<div className="flex items-center justify-between text-sm">
											<div>
												<span className="text-gray-600">Start:</span> <span className="font-medium">{formatDate(contract.start_date, "PP")}</span>
											</div>
											<div>
												<span className="text-gray-600">End:</span> <span className="font-medium text-orange-700">{formatDate(contract.end_date, "PP")}</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>

				{contracts.length > 5 && (
					<div className="mt-4 text-center">
						<Button variant="outline" size="sm">
							View All Expiring Contracts
						</Button>
					</div>
				)}
			</CardContent>
		</Card>
	);
};
